(() => {
  const root = document.querySelector('[data-section-name="pending-approvals-page"]');
  if (!root) return;

  const mount = document.getElementById('PendingApprovalsMount');
  if (!mount) return;

  const companyId = root.dataset.companyId;
  const companyContactId = root.dataset.companyContactId || root.dataset.customerId;
  const customerId = root.dataset.customerId || companyContactId;
  const currentCustomerName = root.dataset.customerName || '';
  const currentLocationId = root.dataset.locationId;

  if (!companyId || !companyContactId) return;

  const locationScript = document.getElementById('PendingApprovalsLocations');
  const availableLocations = locationScript ? JSON.parse(locationScript.textContent || '[]') : [];
  const locationMap = new Map(availableLocations.map((location) => [String(location.id), location]));

  let requisitions = [];
  let approvalInFlight = false;
  let currentMode = 'approver';
  let lastApproverAccess = null;
  function buildApprovalSignature(items) {
    return (items || [])
      .map((item) => `${String(item.variantId)}:${Number(item.quantity || 0)}`)
      .sort()
      .join('|');
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function renderNotice(message, tone = 'neutral') {
    const toneClass = tone === 'error'
      ? ' pending-approvals-page__notice--error'
      : tone === 'success'
        ? ' pending-approvals-page__notice--success'
        : '';

    mount.innerHTML = `<div class="pending-approvals-page__notice${toneClass}">${escapeHtml(message)}</div>`;
  }

  function formatStatus(status) {
    switch (String(status || '').toUpperCase()) {
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return 'Pending approval';
    }
  }

  function getStatusTone(status) {
    switch (String(status || '').toUpperCase()) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'neutral';
    }
  }

  async function parseJsonResponse(response) {
    const text = await response.text();
    const contentType = response.headers.get('content-type') || '';
    const trimmed = text.trim();

    if (trimmed.startsWith('<')) {
      throw new Error(
        `The app proxy returned HTML instead of JSON (HTTP ${response.status}). Reinstall the B2B Order Approval app on this store so the /apps/b2b-order-approval routes are active.`,
      );
    }

    if (contentType.includes('application/json')) {
      return text ? JSON.parse(text) : {};
    }

    return text ? JSON.parse(text) : {};
  }

  async function loadApproverAccess() {
    const params = new URLSearchParams({
      companyId,
      companyContactId,
    });

    if (currentLocationId) params.set('locationId', currentLocationId);

    const response = await fetch(`/apps/b2b-order-approval/approver-access?${params.toString()}`, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });
    const payload = await parseJsonResponse(response);

    if (!response.ok) {
      return { authorized: false, authorizedForCurrentLocation: false };
    }

    return payload;
  }

  function getRequisitionById(id) {
    return requisitions.find((item) => item.id === id) || null;
  }

  function updateUrlSearchParam(key, value) {
    const nextUrl = new URL(window.location.href);

    if (value === null || value === undefined || value === '') {
      nextUrl.searchParams.delete(key);
    } else {
      nextUrl.searchParams.set(key, value);
    }

    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  }

  function renderCards(nextRequisitions, mode = currentMode) {
    if (!nextRequisitions.length) {
      renderNotice(
        mode === 'requester'
          ? 'You have not submitted any requisitions for this company yet.'
          : 'No pending requisitions assigned to this approver right now.'
      );
      return;
    }

    mount.innerHTML = `
      <div class="pending-approvals-page__list">
        ${nextRequisitions.map((requisition) => {
          const locationLabel = requisition.locationName || 'Assigned location';
          const storedRequesterName = requisition.requestingCustomerName && requisition.requestingCustomerName !== 'B2B user'
            ? requisition.requestingCustomerName
            : '';
          const requesterName = (
            storedRequesterName
            || (requisition.requestingCustomerId === companyContactId ? currentCustomerName : '')
            || requisition.requestingCustomerId
            || 'Unknown requester'
          );
          const companyLabel = requisition.companyName || 'B2B Company';
          const statusLabel = formatStatus(requisition.status);
          const statusTone = getStatusTone(requisition.status);
          const showActions = mode === 'approver' && String(requisition.status || '').toUpperCase() === 'PENDING';
          const statusLine = mode === 'requester'
            ? `<div class="pending-approvals-card__line"><strong>Status:</strong> ${escapeHtml(statusLabel)}</div>`
            : '';

          return `
            <article class="pending-approvals-card" data-requisition-id="${escapeHtml(requisition.id)}">
              <div class="pending-approvals-card__badge${statusTone !== 'neutral' ? ` pending-approvals-page__notice--${statusTone}` : ''}">${escapeHtml(mode === 'requester' ? statusLabel : `Order location ${locationLabel}`)}</div>
              <div class="pending-approvals-card__top">
                <div>
                  <div class="pending-approvals-card__line">Company</div>
                  <h3 class="pending-approvals-card__title">${escapeHtml(companyLabel)}</h3>
                </div>
                <h3 class="pending-approvals-card__amount">$${escapeHtml(requisition.total)}</h3>
              </div>
              ${mode === 'approver' ? `<div class="pending-approvals-card__line">Requested by ${escapeHtml(requesterName)}</div>` : ''}
              ${statusLine}
              <div class="pending-approvals-card__line">${escapeHtml(`Order location ${locationLabel}`)}</div>
              <div class="pending-approvals-card__line">Submitted ${escapeHtml(new Date(requisition.submittedAt).toLocaleString())}</div>
              <div class="pending-approvals-card__items">
                ${requisition.items.map((item) => `
                  <div class="pending-approvals-card__item">
                    ${escapeHtml(item.title)} • Qty ${escapeHtml(item.quantity)} • $${escapeHtml(item.lineTotal)}
                  </div>
                `).join('')}
              </div>
              ${showActions ? `
                <div class="pending-approvals-card__actions">
                  <button class="btn" type="button" data-action="approve" data-requisition-id="${escapeHtml(requisition.id)}">
                    <span class="btn__texts">
                      <span class="btn__top-text">Approve</span>
                    </span>
                  </button>
                  <button class="btn btn--secondary" type="button" data-action="reject" data-requisition-id="${escapeHtml(requisition.id)}">
                    <span class="btn__texts">
                      <span class="btn__top-text">Reject</span>
                    </span>
                  </button>
                </div>
              ` : ''}
            </article>
          `;
        }).join('')}
      </div>
    `;
  }

  function renderApproverEmptyState(otherLocationRequisitions = []) {
    if (!otherLocationRequisitions.length) {
      renderNotice('No pending requisitions assigned to this approver right now.');
      return;
    }

    const grouped = new Map();
    for (const requisition of otherLocationRequisitions) {
      const key = String(requisition.companyLocationId || requisition.locationName || 'other');
      const existing = grouped.get(key) || {
        locationName: requisition.locationName || 'another location',
        count: 0,
      };
      existing.count += 1;
      grouped.set(key, existing);
    }

    const summary = [...grouped.values()]
      .map((item) => `${item.count} pending in ${item.locationName}`)
      .join(' • ');

    renderNotice(`No pending approvals in the current location. You still have orders waiting in other approval locations: ${summary}. Switch location to review them.`);
  }

  async function loadRequesterRequisitions() {
    mount.innerHTML = '<div class="pending-approvals-page__loading">Loading pending requisitions...</div>';

    const params = new URLSearchParams({
      companyId,
      requestingCustomerId: customerId,
    });

    const response = await fetch(`/apps/b2b-order-approval/my-requisitions?${params.toString()}`, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });
    const payload = await parseJsonResponse(response);

    if (!response.ok) {
      throw new Error(payload.error || 'Could not load your submitted requisitions.');
    }

    currentMode = 'requester';
    requisitions = payload.requisitions || [];
    renderCards(requisitions, currentMode);
    return requisitions;
  }

  function buildApproverQuery(locationIds = []) {
    const params = new URLSearchParams({
      companyId,
      companyContactId,
    });

    for (const locationId of locationIds) {
      if (locationId) {
        params.append('locationId', String(locationId));
      }
    }

    return params;
  }

  async function loadApproverRequisitions(locationIds = [], { render = true } = {}) {
    mount.innerHTML = '<div class="pending-approvals-page__loading">Loading pending requisitions...</div>';

    const params = buildApproverQuery(locationIds);
    const response = await fetch(`/apps/b2b-order-approval/requisitions?${params.toString()}`, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });
    const payload = await parseJsonResponse(response);

    if (!response.ok) {
      throw new Error(payload.error || 'Could not load pending requisitions.');
    }

    currentMode = 'approver';
    requisitions = payload.requisitions || [];
    if (render) {
      renderCards(requisitions, currentMode);
    }
    return requisitions;
  }

  async function loadApproverCurrentAndOtherRequisitions() {
    const currentLocationRequisitions = await loadApproverRequisitions(currentLocationId ? [currentLocationId] : []);

    if (currentLocationRequisitions.length || !currentLocationId) {
      return currentLocationRequisitions;
    }

    const otherLocationIds = (lastApproverAccess?.locationIds || [])
      .map((id) => String(id))
      .filter((id) => id && id !== String(currentLocationId));

    if (!otherLocationIds.length) {
      renderApproverEmptyState([]);
      return currentLocationRequisitions;
    }

    const otherLocationRequisitions = await loadApproverRequisitions(otherLocationIds, { render: false });
    renderApproverEmptyState(otherLocationRequisitions);
    return [];
  }

  async function clearCart() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('The cart could not be cleared before rebuilding checkout.');
    }
  }

  async function addItemsToCart(requisition) {
    const items = requisition.items.map((item) => ({
      id: Number(item.variantId),
      quantity: Number(item.quantity || 0),
    })).filter((item) => Number.isFinite(item.id) && item.quantity > 0);

    if (!items.length) {
      throw new Error('This requisition does not contain purchasable variants.');
    }

    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    });
    const addPayload = await parseJsonResponse(addResponse);

    if (!addResponse.ok) {
      throw new Error(addPayload.description || addPayload.error || 'Could not rebuild the cart for this requisition.');
    }

    const attributes = {
      'B2B Approval Requisition': requisition.id,
      'B2B Approval Location': requisition.locationName || '',
      'B2B Approval Signature': buildApprovalSignature(requisition.items),
    };
    const updateResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        note: requisition.notes || '',
        attributes,
      }),
    });

    if (!updateResponse.ok) {
      throw new Error('The cart was rebuilt, but the requisition metadata could not be attached.');
    }
  }

  async function approveRequisition(requisitionId) {
    const response = await fetch(`/apps/b2b-order-approval/requisitions/${encodeURIComponent(requisitionId)}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        actingCompanyContactId: companyContactId,
      }),
    });
    const payload = await parseJsonResponse(response);

    if (!response.ok) {
      throw new Error(payload.error || 'Could not approve this requisition.');
    }

    return payload.requisition;
  }

  async function rejectRequisition(requisitionId) {
    const response = await fetch(`/apps/b2b-order-approval/requisitions/${encodeURIComponent(requisitionId)}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        actingCompanyContactId: companyContactId,
      }),
    });
    const payload = await parseJsonResponse(response);

    if (!response.ok) {
      throw new Error(payload.error || 'Could not reject this requisition.');
    }

    return payload.requisition;
  }

  function switchToRequiredLocation(requisitionId, companyLocationId) {
    const location = locationMap.get(String(companyLocationId));

    if (!location?.url) {
      throw new Error('This approver does not have access to the location required for this requisition.');
    }

    const switchUrl = new URL(location.url, window.location.origin);
    const returnUrl = new URL(window.location.pathname, window.location.origin);
    returnUrl.searchParams.set('approveRequisition', requisitionId);
    switchUrl.searchParams.set('return_to', `${returnUrl.pathname}${returnUrl.search}`);
    window.location.href = switchUrl.toString();
  }

  async function beginApproval(requisitionId) {
    if (approvalInFlight) return;

    const requisition = getRequisitionById(requisitionId);
    if (!requisition) {
      renderNotice('This requisition is no longer available in the queue.', 'error');
      return;
    }

    const requisitionLocationId = String(requisition.companyLocationId || '');
    const currentLocation = String(currentLocationId || '');

    if (requisitionLocationId && currentLocation && requisitionLocationId !== currentLocation) {
      switchToRequiredLocation(requisitionId, requisitionLocationId);
      return;
    }

    approvalInFlight = true;
    renderNotice(`Rebuilding checkout for ${requisition.locationName || 'the assigned location'}...`);

    try {
      await clearCart();
      await addItemsToCart(requisition);
      await approveRequisition(requisitionId);
      updateUrlSearchParam('approveRequisition', null);
      window.location.href = `/cart?approvedRequisition=${encodeURIComponent(requisitionId)}`;
    } catch (error) {
      approvalInFlight = false;
      renderNotice(error instanceof Error ? error.message : 'Could not rebuild checkout for this requisition.', 'error');
    }
  }

  async function updateStatus(id, action) {
    try {
      if (action === 'approve') {
        await beginApproval(id);
        return;
      }

      await rejectRequisition(id);
      renderNotice('Requisition rejected.', 'success');
      if (currentMode === 'approver') {
        await loadApproverCurrentAndOtherRequisitions();
      } else {
        await loadRequesterRequisitions();
      }
    } catch (error) {
      renderNotice(error instanceof Error ? error.message : `Could not ${action} requisition.`, 'error');
    }
  }

  mount.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-action]');
    if (!button) return;

    const { action, requisitionId } = button.dataset;
    if (!action || !requisitionId) return;

    button.disabled = true;
    await updateStatus(requisitionId, action);
    button.disabled = false;
  });

  (async () => {
    const params = new URLSearchParams(window.location.search);
    const wasSubmitted = params.get('submitted') === '1';
    const queuedApprovalId = params.get('approveRequisition');

    if (wasSubmitted) {
      renderNotice('Requisition submitted successfully. You can track its status below.', 'success');
    }

    const approverAccess = await loadApproverAccess().catch(() => ({
      authorized: false,
      authorizedForCurrentLocation: false,
    }));
    lastApproverAccess = approverAccess;

    const canApproveInCurrentLocation = currentLocationId
      ? Boolean(approverAccess?.authorizedForCurrentLocation)
      : Boolean(approverAccess?.authorized);

    try {
      if (canApproveInCurrentLocation) {
        await loadApproverCurrentAndOtherRequisitions();
      } else {
        await loadRequesterRequisitions();
      }
    } catch (error) {
      try {
        await loadRequesterRequisitions();
      } catch (requesterError) {
        requisitions = [];
        renderNotice(
          requesterError instanceof Error ? requesterError.message : 'Could not load requisitions.',
          'error',
        );
        return;
      }
    }

    if (queuedApprovalId && currentMode === 'approver') {
      await beginApproval(queuedApprovalId);
    }
  })();
})();
